//'These lists NEEDS to be kept in sync with the ones in CalcRulesServerImpl.vb::GetFieldNameMatch()!
const possibleControlIdPrefixes = ["txt", "span", ".cgf_"];
// When fields are set as Literal, _label are added to the field value in old web
const possiblControlIdSuffixes = ["_label"];
const fieldNameMatchCache = {};
export function getFieldNameMatch(fieldName, controlId) {
    if (fieldName === controlId)
        return controlId;
    const inner = fieldNameMatchCache[fieldName];
    if (inner !== undefined) {
        const cached = inner[controlId];
        if (cached !== undefined)
            return cached ?? undefined;
    }
    for (const prefix of possibleControlIdPrefixes) {
        for (const suffix of possiblControlIdSuffixes) {
            if (`${prefix}${fieldName}${suffix}` === controlId ||
                `${fieldName}${suffix}` === controlId ||
                `${prefix}${fieldName}` === controlId) {
                (fieldNameMatchCache[fieldName] ??= {})[controlId] = fieldName;
                return fieldName;
            }
        }
    }
    (fieldNameMatchCache[fieldName] ??= {})[controlId] = null;
}
export function stripControlIdPrefixAndSuffix(controlId) {
    let fieldName = controlId.trim();
    for (const prefix of possibleControlIdPrefixes) {
        if (fieldName.startsWith(prefix))
            fieldName = fieldName.substring(prefix.length);
    }
    for (const suffix of possiblControlIdSuffixes) {
        if (fieldName.endsWith(suffix))
            fieldName = fieldName.substring(0, fieldName.length - suffix.length);
    }
    return fieldName;
}
export function getResolveReference(reference) {
    if (reference.toLowerCase().startsWith("#resolve:")) {
        return reference.substring(9);
    }
}
export const compose = (fn1 = a => a, ...fns) => {
    return (a) => {
        let o = fn1(a);
        for (const fn of fns) {
            o = fn(o);
        }
        return o;
    };
};
export function composeFirstParam(...funcs) {
    return (input, ...other) => compose(...funcs.map(f => (i) => f(i, ...other)))(input);
}
export function isNotNull(x) {
    return x != null;
}
export const isEqualOrUndefined = (a, b) => a === undefined || b === undefined || a === b;
export const shallowClone = (a) => ({ ...a });
export function isResolvedCtrl(ctrlField) {
    return (ctrlField != null && ctrlField.ref != null);
}
export function isVariableField(ctrlField) {
    return ctrlField != null && ctrlField.variable != null;
}
export function getInNamespaced(input, namespace, name, defaultValue) {
    if (!(namespace && name))
        return defaultValue;
    const value = input?.[namespace]?.[name];
    return value !== undefined ? value : defaultValue;
}
export function existsInNamespaced(input, namespace, name) {
    if (!(namespace && name))
        return false;
    return input?.[namespace]?.[name] !== undefined;
}
export function setInMutableNamespacedCopy(original, copy, namespace, name, value) {
    if (!(namespace && name) ||
        getInNamespaced(copy || original, namespace, name) === value) {
        return copy || original;
    }
    const mutable = !copy || copy === original
        ? shallowClone(original)
        : copy;
    let namespaceObject = mutable[namespace];
    if (!namespaceObject || namespaceObject === original?.[namespace]) {
        namespaceObject = shallowClone(namespaceObject);
    }
    namespaceObject[name] = value;
    mutable[namespace] = namespaceObject;
    return mutable;
}
export function getKeysInNamespace(input, namespace) {
    const nsRecord = ((input && input[namespace]) || {});
    return Object.keys(nsRecord);
}
function isKeyOf(obj, key) {
    return key && obj[key] !== undefined;
}
export function caseInsensitiveMap(obj) {
    const lowerCaseToCorrectCaseLookup = new Map(Object.keys(obj).map(k => [k.toLowerCase(), k]));
    return {
        getEntry(key) {
            if (key) {
                if (isKeyOf(obj, key))
                    return [key, obj[key]];
                if (typeof key === "string") {
                    const correctCaseKey = lowerCaseToCorrectCaseLookup.get(key.toLowerCase());
                    if (isKeyOf(obj, correctCaseKey))
                        return [correctCaseKey, obj[correctCaseKey]];
                }
            }
            return [undefined, undefined];
        }
    };
}
export function mapValueToKey(obj, getKey = x => x) {
    return Object.entries(obj).reduce((acc, [key, item]) => {
        const newKey = item != null && getKey(item, acc);
        if (newKey)
            acc[newKey] = key;
        return acc;
    }, {});
}
/**
 * a === b or both are NaN
 *
 * (NaN === NaN) -> false
 *
 * @param a
 * @param b
 */
export function isEqualOrBothNaN(a, b) {
    return a === b || (Number.isNaN(a) && Number.isNaN(b));
}
const filterToFieldNameAlias = {
    ChasisNumber: "ChassisNumber",
    Milage: "Mileage",
    PriceKmExceeded: "ContractMileageOverridePrice,PriceKmExceeded",
    KmTotal: "ExpectedMileage",
    FirstDueDay: "FirstDueDate",
    CommissionWindowLink: "CommissionsToggle",
    Commission: "CommissionsResults",
    CalcInterestButton: "EnableInterestCalculation",
    FundingInterest: "FundingInterestId"
};
export function filterNameToFieldName(filterName) {
    return filterToFieldNameAlias[filterName] || filterName;
}
export function withGetSet(original, copy, namespace, onSet) {
    const hasGetSet = {
        get(key) {
            return getInNamespaced(copy || original, namespace, key);
        },
        set(key, value) {
            copy = setInMutableNamespacedCopy(original, copy, namespace, key, value);
            if (onSet)
                onSet(key, value);
            return this;
        },
        getFinal() {
            return copy;
        },
        getLastChanged() {
            // NOTE: For this method to work a variable named "LastChangedField"
            // must exist in the namespace.
            return getInNamespaced(copy || original, namespace, "LastChangedField");
        }
    };
    return hasGetSet;
}
export function withGet(obj, namespace) {
    return {
        get: (key) => getInNamespaced(obj, namespace, key),
        set() {
            return this;
        }
    };
}
export function withGetFromField(input, obj, namespace) {
    return {
        get: (key) => {
            const ctrl = getInNamespaced(input.variableControlMaps, namespace, key);
            const fieldRef = getInNamespaced(input.controlFieldMaps, namespace, ctrl);
            const fieldNamespace = isResolvedCtrl(fieldRef)
                ? fieldRef.namespace
                : namespace;
            return getInNamespaced(obj, fieldNamespace, fieldRef.fieldName);
        },
        set() {
            return this;
        }
    };
}
//# sourceMappingURL=utils.js.map